import { expectExternalAuthorizationResult } from '@pressreader/authentication-api';
import { EXTERNAL_AUTH_ERROR, EXTERNAL_AUTH_TYPE } from '@pressreader/authentication-types';
import { localizedString, localizedStringWithDefault } from '@pressreader/resources';
import { baseUrl as svcBaseUrl, httpGet, httpPost, svcAuth } from '@pressreader/services';
import { newGuid } from '@pressreader/utils';

import ndAlert from 'nd.alert';
import { logError } from 'shared/errors/error.handlers';

interface AuthContext {
    command?: string;
    returnUrl?: string | null;
    linkToCurrentAccount?: boolean;
    library?: string | null;
    state?: string | null;
    isWindowMode?: boolean;
    [key: string]: unknown;
}

interface AuthorizationRequestUrlArgs {
    provider: string;
    key: string;
    token: string;
    authtype: string;
    context?: AuthContext;
}

interface RequestAuthorizationArgs {
    provider: string;
    authtype?: string;
    key?: string;
    url: string;
    context: AuthContext;
}

interface CompleteAuthorizationArgs {
    provider?: string;
    authtype: string;
    key: string;
    authResult: unknown;
    context?: AuthContext;
}

interface AuthorizeArgs {
    provider: string;
    authtype?: string;
    context?: AuthContext;
}

/**
 * Default implementation of authorization provider.
 * request key -> token -> show authorization window -> process a result over cookie -> sign in by key.
 * Fits most OAuth 2.0 cases.
 */
class DefaultExternalAuthProvider {
    getRequestKey(): string {
        // Key format is a guid today, but the server treats it opaquely.
        // Native app generates it client-side too, so we follow suit and save a round-trip.
        return newGuid();
    }

    getToken(): string {
        return svcAuth.token();
    }

    getAuthorizationRequestUrl({ provider, key, token, authtype, context = {} }: AuthorizationRequestUrlArgs): string {
        const baseUrl = svcBaseUrl();
        const path = '/externalauth/requestauthorization/';
        const tkn = encodeURIComponent(token);
        const params: Record<string, unknown> = {
            key,
            provider,
            authtype,
            command: context?.command,
            returnUrl: context?.returnUrl || null,
            linkToCurrentAccount: context?.linkToCurrentAccount === true,
            libraryId: context?.library || null,
            state: context?.state || null,
        };

        const queryString = Object.keys(params)
            .filter(name => params[name] != null)
            .map(name => `${name}=${params[name]}`)
            .join('&');

        return `${baseUrl}${tkn}${path}?${queryString}`;
    }

    /**
     * Requests authorization.
     * Must run in the same task as the user action (no async work before window.open).
     */
    requestAuthorization({ provider, url, context }: RequestAuthorizationArgs): Promise<unknown> {
        // standalone window mode, out of pressreader site, used by native apps
        if (context.isWindowMode) {
            window.location.href = url;
            return Promise.resolve();
        }

        let wnd = window.open(url);

        return wnd
            ? expectExternalAuthorizationResult(wnd)
            : // browser blocked the popup; require an explicit user click via confirmation
              ndAlert()
                  .confirm(
                      localizedStringWithDefault(`Dialogs.Confirm.Logon.${provider}`, localizedString('Dialogs.Confirm.Logon')),
                      localizedString('Buttons.Cancel.Text'),
                      localizedString('Buttons.Login.Text'),
                  )
                  .then(() => {
                      wnd = window.open(url);
                      return expectExternalAuthorizationResult(wnd);
                  });
    }

    signinByKey(key: string) {
        return httpPost('auth', 'SigninByKey', JSON.stringify({ key }), {
            contentType: 'application/json',
        });
    }

    releaseRequestKey(key: string) {
        // removes the session key from temp storage server-side
        return httpGet('ExternalAuth', 'ValidateRequestKey', { key }).catch(logError);
    }

    completeAuthorization({ authtype, key, authResult }: CompleteAuthorizationArgs): Promise<unknown> {
        if (!authResult) {
            return Promise.resolve();
        }
        return authtype === EXTERNAL_AUTH_TYPE.SIGNUP
            ? this.signinByKey(key).then(signInResult => ({ authResult, signInResult }))
            : this.releaseRequestKey(key).then(() => ({ authResult }));
    }

    async authorize({ provider, authtype = EXTERNAL_AUTH_TYPE.SHARING, context = {} }: AuthorizeArgs) {
        const key = this.getRequestKey();
        const token = this.getToken();
        const url = this.getAuthorizationRequestUrl({ provider, authtype, key, token, context });

        try {
            const authResult = await this.requestAuthorization({ provider, authtype, key, url, context });
            return await this.completeAuthorization({ provider, authtype, key, authResult, context });
        } catch (error) {
            // hide unexpected errors from client: reject without a value so callers
            // that branch on `!error` (e.g. nd.authentication.service.ts) treat it as
            // a silent dismissal instead of surfacing an alert. `return Promise.reject()`
            // (not `throw undefined`) avoids Sonar S3696.
            if (error && (error as { code?: string }).code === EXTERNAL_AUTH_ERROR.UNEXPECTED_ERROR) {
                logError(error);
                return Promise.reject();
            }
            throw error;
        }
    }
}

export { AuthContext, AuthorizationRequestUrlArgs, AuthorizeArgs, CompleteAuthorizationArgs, RequestAuthorizationArgs };
export default DefaultExternalAuthProvider;
